import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { getCallToken, getSingleAppointment } from "../../store/appointment/appointmentActions";
import { CountdownTimer } from "../../components/course/counterdown";
import { getImage } from "../../utils";
import moment from "moment";
import { FiArrowLeft } from "react-icons/fi";
import { useCallSession } from "../video/callSessionContext";
import { RescheduleEvent } from "../../components/course/reschedule";
import { CancelEvent } from "../../components/course/cancel";
import { ApproveEvent } from "../../components/course/approve";
import { ReportEvent } from "../../components/course/report";
import { CourseHistory } from "./history";
import { Toaster, toast } from "react-hot-toast";
import { ProfileRole } from "../../store/profile/profileSlice";

const formatTimestamp = (value) => {
    if (!value) return "N/A";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : "N/A";
};

const formatDuration = (seconds) => {
    if (seconds == null || Number.isNaN(Number(seconds))) return "N/A";
    const total = Number(seconds);
    const mins = Math.floor(total / 60);
    const secs = Math.floor(total % 60);
    return `${mins}m ${secs}s`;
};

const getLatestRescheduleNewDate = (appointment) => {
    const entries = Array.isArray(appointment?.reschedule) ? appointment.reschedule : [];
    for (let i = entries.length - 1; i >= 0; i -= 1) {
        if (entries[i]?.newDate) return entries[i].newDate;
    }
    return null;
};

const getSessionBounds = (appointment) => {
    if (appointment?.startUtc) {
        const start = moment(appointment.startUtc).parseZone();
        const end = appointment?.endUtc
            ? moment(appointment.endUtc).parseZone()
            : moment(start).add(50, "minutes");
        return { start, end };
    }

    const rescheduleNewDate = getLatestRescheduleNewDate(appointment);
    if (rescheduleNewDate) {
        const start = moment(rescheduleNewDate).parseZone();
        return { start, end: moment(start).add(50, "minutes") };
    }

    if (!appointment?.dateUtc) {
        return { start: null, end: null };
    }
    const start = moment(appointment.dateUtc).parseZone();
    return { start, end: moment(start).add(50, "minutes") };
};

const getJoinWindowState = (appointment, currentTime) => {
    if (!appointment || appointment.status !== -1) {
        return { canJoin: false, isBeforeWindow: false, isAfterWindow: false };
    }

    const { start, end } = getSessionBounds(appointment);
    if (!start || !end) {
        return { canJoin: false, isBeforeWindow: false, isAfterWindow: false };
    }

    const joinOpenAt = moment(start).subtract(5, "minutes");
    const joinCloseAt = end;

    return {
        canJoin: currentTime.isBetween(joinOpenAt, joinCloseAt, undefined, "[]"),
        isBeforeWindow: currentTime.isBefore(joinOpenAt),
        isAfterWindow: currentTime.isAfter(joinCloseAt),
    };
};

const getCourseTimingState = (appointment, currentTime) => {
    const { start, end } = getSessionBounds(appointment);
    if (!start || !end) {
        return { phase: "upcoming", start: null, end: null };
    }
    if (currentTime.isBefore(start)) return { phase: "upcoming", start, end };
    if (currentTime.isSameOrBefore(end)) return { phase: "in_progress", start, end };
    return { phase: "ended", start, end };
};

const StatusTag = ({ status }) => {
    if (status === -1) return <span className="bg-nt-blue text-white px-2 rounded py-1">Pending</span>;
    if (status === 0) return <span className="bg-nt-blue text-white px-2 rounded py-1">Pending Payment</span>;
    if (status === 1) return <span className="bg-green text-white px-2 rounded py-1">Done</span>;
    if (status === 2) return <span className="bg-red text-white px-2 rounded py-1">Canceled</span>;
    if (status === 3) return <span className="bg-green opacity-70 text-white px-2 rounded py-1">Under Review</span>;
    if (status === 4) return <span className="bg-red opacity-70 text-white px-2 rounded py-1">Payment Rejected</span>;
    return null;
};

const CourseInfo = ({ appointment }) => (
    <div className="flex flex-col md:flex-row justify-between items-center p-5 border-b border-nt-grey-light bg-gradient-to-r from-white to-slate-50 rounded-t-2xl">
        <div className="flex gap-4 items-center">
            <img src={getImage(appointment.avatar, "AVATAR")} className="w-14 object-cover h-14 rounded-full border border-nt-grey-light" alt="" />
            <div>
                <h4 className="text-nt-blue capitalize font-semibold text-lg">{appointment.fullname}</h4>
                <span className="text-nt-grey text-sm block">Date: {moment(appointment.date).utc().format("YYYY-MM-DD HH:mm")}</span>
            </div>
        </div>
        <div className="flex flex-row-reverse gap-2 mt-3 md:mt-0 items-center">
            <StatusTag status={appointment.status} />
            <h3 className="text-nt-blue text-right font-semibold">{appointment.price} €</h3>
        </div>
    </div>
);

const CallAnalyticsPanel = ({ analytics }) => {
    if (!analytics) {
        return (
            <div className="mt-5 border border-nt-grey-light rounded-xl p-4 bg-slate-50">
                <h4 className="text-nt-blue font-semibold">Call Analytics</h4>
                <p className="text-nt-grey text-sm mt-1">No call analytics available yet.</p>
            </div>
        );
    }

    const members = Array.isArray(analytics.members) ? analytics.members : [];
    const status = analytics.status || "pending";
    const sourceLabel = analytics.source ? ` · ${analytics.source}` : "";

    return (
        <div className="mt-5 border border-nt-grey-light rounded-xl p-4 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h4 className="text-nt-blue font-semibold">Call Analytics</h4>
                <span className="text-xs px-2 py-1 rounded-full bg-nt-grey-light text-nt-blue capitalize">
                    {status}{sourceLabel}
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-nt-grey">Session duration</p>
                    <p className="text-sm font-semibold text-nt-blue">{formatDuration(analytics?.summary?.durationSeconds)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-nt-grey">Participants</p>
                    <p className="text-sm font-semibold text-nt-blue">{analytics?.summary?.participantCount ?? members.length ?? 0}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-nt-grey">Overall quality</p>
                    <p className="text-sm font-semibold text-nt-blue capitalize">{analytics?.summary?.quality || "N/A"}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-nt-grey">Channel</p>
                    <p className="text-xs font-semibold text-nt-blue truncate">{analytics.channel || "N/A"}</p>
                </div>
            </div>
            {analytics.fetchedAt ? (
                <p className="text-xs text-nt-grey mb-3">Fetched: {formatTimestamp(analytics.fetchedAt)}</p>
            ) : null}
            {status === "failed" ? (
                <p className="text-sm text-red mb-3">{analytics?.errorMessage || "Failed to fetch analytics from provider."}</p>
            ) : null}
            {status === "pending" ? (
                <p className="text-sm text-nt-grey mb-3">Analytics collection is in progress. Please check again shortly.</p>
            ) : null}
            {members.length > 0 ? (
                <div className="space-y-2">
                    {members.map((member, index) => (
                        <div key={`${member.uid}-${index}`} className="border border-nt-grey-light rounded-lg p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-nt-blue capitalize">{member.role || "Unknown"}</p>
                                <div className="flex items-center gap-2 text-xs text-nt-grey">
                                    <span className="capitalize">Quality: {member.quality || "N/A"}</span>
                                    <span>UID: {member.uid}</span>
                                </div>
                            </div>
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <p className="text-nt-grey"><span className="text-nt-blue font-medium">Joined:</span> {formatTimestamp(member.joinTime)}</p>
                                <p className="text-nt-grey"><span className="text-nt-blue font-medium">Left:</span> {formatTimestamp(member.leaveTime)}</p>
                                <p className="text-nt-grey"><span className="text-nt-blue font-medium">Duration:</span> {formatDuration(member.durationSeconds)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-nt-grey">No member-level call records are available.</p>
            )}
        </div>
    );
};

const PendingCourse = ({
    appointment,
    currentTime,
    isJoining,
    isReschedule,
    isCancel,
    onJoin,
    onReschedule,
    onCancel,
    onRefresh,
    setIsReschedule,
    setIsCancel,
}) => {
    const { canJoin, isBeforeWindow, isAfterWindow } = getJoinWindowState(appointment, currentTime);
    const { phase, start, end } = getCourseTimingState(appointment, currentTime);
    const shouldShowJoinButton = !isBeforeWindow && !isAfterWindow;
    const joinLabel = isJoining ? "Joining..." : "Join Course";

    return (
        <div className="rounded-2xl overflow-hidden border border-nt-grey-light bg-white shadow-sm">
            <CourseInfo appointment={appointment} />
            <div className="px-5 pt-5 space-y-4">
                {phase === "upcoming" && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl py-4">
                        <CountdownTimer target={start} text="Course starts in" />
                    </div>
                )}
                {phase === "upcoming" && isBeforeWindow && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
                        <p className="text-sm text-amber-700 font-medium">
                            Join button appears 5 minutes before the course starts.
                        </p>
                    </div>
                )}
                {phase === "in_progress" && (
                    <div className="bg-green bg-opacity-10 border border-green rounded-xl p-4 text-center mb-5">
                        <h4 className="text-green font-semibold">Course is in progress</h4>
                        <p className="text-nt-grey text-sm">Started at {start.format("HH:mm")} and ends at {end.format("HH:mm")}</p>
                    </div>
                )}
                {phase === "ended" && (
                    <div className="bg-nt-grey-light border border-nt-grey rounded-xl p-4 text-center mb-5">
                        <h4 className="text-nt-blue font-semibold">Course time ended</h4>
                        <p className="text-nt-grey text-sm">This session ended at {end.format("HH:mm")}</p>
                    </div>
                )}
            </div>
            {(!isReschedule && !isCancel) && (
                <div className="flex justify-center gap-4 my-5 flex-wrap px-5">
                    {shouldShowJoinButton ? (
                        <button
                            type="button"
                            onClick={onJoin}
                            disabled={!canJoin || isJoining}
                            className={`w-44 py-2.5 rounded-lg text-white font-medium transition ${canJoin && !isJoining ? "bg-green hover:opacity-90" : "bg-nt-grey opacity-80 cursor-not-allowed"}`}
                        >
                            {isAfterWindow ? "Closed" : joinLabel}
                        </button>
                    ) : null}
                    {phase === "upcoming" ? (
                        <button type="button" onClick={onReschedule} className="w-32 py-2.5 bg-nt-blue rounded-lg text-white font-medium hover:opacity-90 transition shadow-sm">Reschedule</button>
                    ) : null}
                    <button type="button" onClick={onCancel} className="w-32 py-2.5 bg-nt-red rounded-lg text-white font-medium hover:opacity-90 transition shadow-sm">Cancel</button>
                </div>
            )}
            {isReschedule && <RescheduleEvent refreshParent={onRefresh} setIsReschedule={setIsReschedule} event={appointment} />}
            {isCancel && <CancelEvent refreshParent={onRefresh} setIsCancel={setIsCancel} event={appointment} />}
        </div>
    );
};

const PendingPayment = ({
    appointment,
    currentProfile,
    isApprove,
    isReport,
    setIsApprove,
    setIsReport,
    onRefresh,
}) => {
    const { end } = getSessionBounds(appointment);
    const paymentDueTarget = end
        ? moment(end).add(2, "day")
        : moment(appointment.dateUtc).parseZone().add(50, "minutes").add(2, "day");

    return (
        <div>
            <CourseInfo appointment={appointment} />
            <div className="px-5 pt-4">
                <div className="bg-nt-grey-light rounded-xl py-4">
                    <CountdownTimer text="Payment due in" target={paymentDueTarget} />
                </div>
            </div>
            {(!isApprove && !isReport) && (
                <div className="flex justify-center gap-5 my-5">
                    {(currentProfile.role && currentProfile.role === ProfileRole.STUDENT) ? (
                        <button type="button" className="w-40 py-2.5 bg-nt-blue rounded-lg text-white font-medium hover:opacity-90 transition" onClick={() => setIsApprove(true)}>Approve Payment</button>
                    ) : null}
                    <button type="button" className="w-40 py-2.5 bg-nt-red rounded-lg text-white font-medium hover:opacity-90 transition" onClick={() => setIsReport(true)}>Report Issue</button>
                </div>
            )}
            {isApprove && <ApproveEvent refreshParent={onRefresh} setIsApprove={setIsApprove} event={appointment} />}
            {isReport && <ReportEvent refreshParent={onRefresh} setIsReport={setIsReport} event={appointment} />}
        </div>
    );
};

const Done = ({ appointment }) => (
    <div>
        <CourseInfo appointment={appointment} />
        <div className="px-5 py-5">
            <div className="bg-green bg-opacity-10 border border-green rounded-xl p-4 text-center">
                <h4 className="text-green font-semibold">Course completed</h4>
                <p className="text-nt-grey text-sm">This appointment is finished.</p>
            </div>
        </div>
    </div>
);

const Canceled = ({ appointment, by }) => (
    <div>
        <CourseInfo appointment={appointment} />
        <div className="px-5 py-5">
            <div className="bg-red bg-opacity-10 border border-red rounded-xl p-4 text-center">
                <h4 className="text-red font-semibold">Appointment canceled</h4>
                <p className="text-nt-grey text-sm">Canceled by {by}</p>
            </div>
        </div>
    </div>
);

const ReviewAction = ({
    isSendingReview,
    reviewResult,
    onReview,
    onConfirm,
    onCancelReview,
}) => (
    !isSendingReview ? (
        <div className="flex justify-center gap-5">
            <button type="button" className="text-white bg-nt-blue py-2 px-4 rounded-md" onClick={() => onReview(true)}>Pay Tutor</button>
            <button type="button" className="text-white bg-nt-red py-2 px-4 rounded-md" onClick={() => onReview(false)}>Refund student</button>
        </div>
    ) : (
        <div>
            <p className="text-nt-blue text-center mb-3">{reviewResult ? "Are you sure to pay tutor ?" : "Are you sure to refund student ?"}</p>
            <div className="justify-center flex gap-5">
                <button type="button" onClick={onConfirm} className="text-white bg-nt-blue py-2 px-4 rounded-md">Yes</button>
                <button type="button" onClick={onCancelReview} className="text-nt-blue bg-nt-grey-light py-2 px-4 rounded-md">Cancel</button>
            </div>
        </div>
    )
);

const UnderReview = ({
    appointment,
    currentProfile,
    isSendingReview,
    reviewResult,
    onReview,
    onConfirm,
    onCancelReview,
}) => (
    <div>
        <CourseInfo appointment={appointment} />
        <div className="px-5 py-5">
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-center">
                <h4 className="text-nt-blue font-semibold">Payment under review</h4>
            </div>
        </div>
        {currentProfile?.role === 1 ? (
            <div className="mt-5 pt-3 border-t-2">
                <ReviewAction
                    isSendingReview={isSendingReview}
                    reviewResult={reviewResult}
                    onReview={onReview}
                    onConfirm={onConfirm}
                    onCancelReview={onCancelReview}
                />
            </div>
        ) : null}
    </div>
);

const PaymentRejected = ({ appointment }) => (
    <div>
        <CourseInfo appointment={appointment} />
        <div className="px-5 py-5">
            <div className="bg-red bg-opacity-10 border border-red rounded-xl p-4 text-center">
                <h4 className="text-red font-semibold">Payment rejected</h4>
            </div>
        </div>
    </div>
);

const CourseDetailsBody = ({
    appointment,
    currentProfile,
    currentTime,
    isJoining,
    isReschedule,
    isCancel,
    isApprove,
    isReport,
    isSendingReview,
    reviewResult,
    onJoin,
    setIsReschedule,
    setIsCancel,
    setIsApprove,
    setIsReport,
    onRefresh,
    onReview,
    onConfirmReview,
    onCancelReview,
    onBack,
}) => {
    const { phase } = getCourseTimingState(appointment, currentTime);
    const shouldShowAnalytics = appointment.status !== -1 || phase === "ended";
    let globalStatus = null;

    switch (appointment.status) {
        case -1:
            globalStatus = (
                <PendingCourse
                    appointment={appointment}
                    currentTime={currentTime}
                    isJoining={isJoining}
                    isReschedule={isReschedule}
                    isCancel={isCancel}
                    onJoin={onJoin}
                    onReschedule={() => setIsReschedule(true)}
                    onCancel={() => setIsCancel(true)}
                    onRefresh={onRefresh}
                    setIsReschedule={setIsReschedule}
                    setIsCancel={setIsCancel}
                />
            );
            break;
        case 0:
            globalStatus = (
                <PendingPayment
                    appointment={appointment}
                    currentProfile={currentProfile}
                    isApprove={isApprove}
                    isReport={isReport}
                    setIsApprove={setIsApprove}
                    setIsReport={setIsReport}
                    onRefresh={onRefresh}
                />
            );
            break;
        case 1:
            globalStatus = <Done appointment={appointment} />;
            break;
        case 2:
            globalStatus = (
                <Canceled
                    appointment={appointment}
                    by={appointment.cancel?.by === currentProfile._id ? "you" : appointment.otherName}
                />
            );
            break;
        case 3:
            globalStatus = (
                <UnderReview
                    appointment={appointment}
                    currentProfile={currentProfile}
                    isSendingReview={isSendingReview}
                    reviewResult={reviewResult}
                    onReview={onReview}
                    onConfirm={onConfirmReview}
                    onCancelReview={onCancelReview}
                />
            );
            break;
        case 4:
            globalStatus = <PaymentRejected appointment={appointment} />;
            break;
        default:
            break;
    }

    return (
        <>
            <button type="button" className="flex items-center no-underline gap-2 text-nt-grey" onClick={onBack}>
                <FiArrowLeft />Back
            </button>
            {globalStatus}
            {shouldShowAnalytics ? <CallAnalyticsPanel analytics={appointment?.callAnalytics} /> : null}
            <CourseHistory
                otherName={appointment.otherName}
                history={[
                    ...(appointment.report && appointment.report.by ? [{ ...appointment.report, type: "report" }] : []),
                    ...(appointment.cancel ? [{ ...appointment.cancel, type: "cancel" }] : []),
                    ...(appointment.approve ? [{ ...appointment.approve, type: "approve" }] : []),
                    ...(appointment.reschedule ? [...appointment.reschedule.map((v) => ({ ...v, type: "reschedule" }))] : []),
                ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
            />
        </>
    );
};

const CourseDetails = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentProfile } = useSelector((state) => state.profile);
    const [loading, setLoading] = useState(true);
    const [appointment, setAppointment] = useState(null);
    const [isReschedule, setIsReschedule] = useState(false);
    const [isCancel, setIsCancel] = useState(false);
    const [isApprove, setIsApprove] = useState(false);
    const [isReport, setIsReport] = useState(false);
    const [isJoining, setIsJoining] = useState(false);
    const [clockTick, setClockTick] = useState(0);
    const { startCall, callSession } = useCallSession() || {};

    const [isSendingReview, setIsSendingReview] = useState(false);
    const [reviewResult, setReviewResult] = useState(false);

    useEffect(() => {
        handleInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isReschedule || isCancel || isApprove || isReport || isSendingReview || callSession) {
            return undefined;
        }
        const intervalId = setInterval(() => {
            setClockTick((v) => v + 1);
        }, 30_000);
        return () => clearInterval(intervalId);
    }, [isReschedule, isCancel, isApprove, isReport, isSendingReview, callSession]);

    // clockTick forces a fresh "now" for join-window UI without remounting children
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const currentTime = useMemo(() => moment(), [clockTick]);

    const handleInit = () => {
        setIsReschedule(false);
        dispatch(getSingleAppointment(id))
            .unwrap()
            .then((v) => {
                setAppointment(v);
                setLoading(false);
            });
    };

    const handleReview = (result) => {
        setIsSendingReview(true);
        setReviewResult(result);
    };

    const dispatchReview = () => {};

    const handleJoinCourse = () => {
        if (!appointment?._id || isJoining) return;

        setIsJoining(true);
        dispatch(getCallToken(appointment._id))
            .unwrap()
            .then((callData) => {
                startCall({
                    channel: callData.channel,
                    token: callData.token,
                    uid: callData.uid,
                    joinCloseAt: callData.joinCloseAt,
                    joinOpenAt: callData.joinOpenAt,
                    serverNow: callData.serverNow,
                    appointmentId: appointment._id,
                });
                navigate("/appointment/chat");
            })
            .catch((error) => {
                const message = error?.error || "Unable to join this appointment right now";
                toast.error(message);
            })
            .finally(() => {
                setIsJoining(false);
            });
    };

    return (
        <div className="bg-gradient-to-b from-white to-slate-50 xl:min-w-[760px] lg:w-2/3 mx-auto rounded-2xl p-5 shadow-md border border-slate-200">
            {!loading ? (
                <>
                    <Toaster />
                    <CourseDetailsBody
                        appointment={appointment}
                        currentProfile={currentProfile}
                        currentTime={currentTime}
                        isJoining={isJoining}
                        isReschedule={isReschedule}
                        isCancel={isCancel}
                        isApprove={isApprove}
                        isReport={isReport}
                        isSendingReview={isSendingReview}
                        reviewResult={reviewResult}
                        onJoin={handleJoinCourse}
                        setIsReschedule={setIsReschedule}
                        setIsCancel={setIsCancel}
                        setIsApprove={setIsApprove}
                        setIsReport={setIsReport}
                        onRefresh={handleInit}
                        onReview={handleReview}
                        onConfirmReview={dispatchReview}
                        onCancelReview={() => setIsSendingReview(false)}
                        onBack={() => navigate(-1)}
                    />
                </>
            ) : null}
        </div>
    );
};

export { CourseDetails };
